import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, Users, Loader } from 'lucide-react';

// Animation sequence data with fictional people
const animationSequence = [
  {
    type: 'user-input',
    text: 'Find fintech professionals in Boston',
    delay: 2000
  },
  {
    type: 'ai-response',
    delay: 1800,
    content: {
      text: "Great! I found several valuable fintech professionals in the Boston area with expertise in blockchain, cryptocurrency, and digital finance. These connections combine strong educational backgrounds with real-world industry experience in emerging technologies.",
      connections: [
        {
          name: 'Marcus Thompson',
          current_role: 'VP of Digital Assets',
          company: 'FinTech Innovations',
          location: 'Boston, MA',
          reasoning: "Harvard MBA with extensive fintech and blockchain expertise. Previously led crypto strategy at major financial institutions. Active mentor in Boston's fintech community and frequent speaker at industry events.",
          priority: 'high',
          common_ground: 'Deeply invested in fintech education and Web3 innovation; regularly mentors aspiring blockchain professionals'
        },
        {
          name: 'Jennifer Park',
          current_role: 'Blockchain Solutions Architect',
          company: 'CryptoScale Labs',
          location: 'Cambridge, MA',
          reasoning: "MIT graduate who pioneered blockchain implementations for enterprise clients. Recognized expert with cryptocurrency and DeFi expertise gained through academic research and practical consulting work.",
          priority: 'high',
          common_ground: 'Cutting-edge blockchain knowledge; built educational resources for crypto and fintech professionals'
        },
        {
          name: 'David Martinez',
          current_role: 'Director of Financial Technology',
          company: 'Atlantic Digital Bank',
          location: 'Boston, MA',
          reasoning: 'Seasoned banking professional with focus on digital transformation and emerging payment technologies. Led multiple successful fintech integration projects.',
          priority: 'medium',
          common_ground: 'Boston fintech ecosystem leader with extensive banking and financial services experience'
        }
      ]
    }
  },
  {
    type: 'user-input',
    text: 'Now find me VCs in San Francisco',
    delay: 4000
  },
  {
    type: 'ai-response',
    delay: 1800,
    content: {
      text: "Absolutely! I'm searching for venture capitalists in San Francisco with expertise in early-stage investing and tech startups. These VCs are active in the Bay Area ecosystem and have strong track records.",
      connections: [
        {
          name: 'Rebecca Chen',
          current_role: 'Partner',
          company: 'Horizon Ventures',
          location: 'San Francisco, CA',
          reasoning: 'Stanford MBA with 12+ years in VC. Led investments in multiple unicorn startups including fintech and SaaS companies. Active mentor at prominent startup accelerators.',
          priority: 'high',
          common_ground: 'Focuses on early-stage B2B SaaS and fintech, regularly speaks at startup conferences'
        },
        {
          name: 'James Sullivan',
          current_role: 'General Partner',
          company: 'Velocity Capital Partners',
          location: 'Menlo Park, CA',
          reasoning: 'Former founder turned VC specializing in enterprise software and AI infrastructure. Known for hands-on approach with portfolio companies and strong network in Silicon Valley.',
          priority: 'high',
          common_ground: 'MIT alumnus passionate about technical founders; hosts monthly founder dinners'
        },
        {
          name: 'Priya Sharma',
          current_role: 'Principal',
          company: 'NextGen Ventures',
          location: 'San Francisco, CA',
          reasoning: 'Rising star in VC focusing on pre-seed and seed stage companies. Previously led product at major tech companies. Strong advocate for diverse founding teams.',
          priority: 'medium',
          common_ground: 'Active on social media sharing insights on product-market fit; judges startup pitch competitions'
        }
      ]
    }
  }
];

const AIConnectorDemo = () => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const chatEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const animationTimeoutRef = useRef(null);

  // Scroll to bottom when messages change
    useEffect(() => {
    if (chatEndRef.current && chatEndRef.current.parentElement) {
        const container = chatEndRef.current.parentElement;
        container.scrollTop = container.scrollHeight;
    }
    }, [messages]);
  // Start animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      startAnimation();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Priority color helper
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return { bg: '#dcfce7', text: '#166534' };
      case 'medium':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'low':
        return { bg: '#e0e7ff', text: '#3730a3' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  // Type user message animation
  const typeUserMessage = (text, callback) => {
    let index = 0;
    setUserInput(''); // Start with empty string, not undefined

    const typingInterval = setInterval(() => {
      if (index < text.length) {
        setUserInput(text.substring(0, index + 1)); // Use substring instead of concatenation
        index++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          callback();
        }, 300);
      }
    }, 50);
  };

  // Add user message
  const addUserMessage = (text, callback) => {
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: text
      }
    ]);
    setUserInput('');

    if (callback) {
      setTimeout(callback, 300);
    }
  };

  // Add AI response
  const addAIResponse = (content, callback) => {
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: content.text,
          suggestions: content.connections || []
        }
      ]);

      if (callback) {
        setTimeout(callback, 500);
      }
    }, 1800);
  };

  // Execute animation step
  const executeStep = (step) => {
    if (!step) {
      // Animation complete, restart after delay
      animationTimeoutRef.current = setTimeout(() => {
        resetAnimation();
      }, 5000);
      return;
    }

    if (step.type === 'user-input') {
      animationTimeoutRef.current = setTimeout(() => {
        typeUserMessage(step.text, () => {
          addUserMessage(step.text, () => {
            setCurrentStep((prev) => prev + 1);
          });
        });
      }, step.delay);
    } else if (step.type === 'ai-response') {
      animationTimeoutRef.current = setTimeout(() => {
        addAIResponse(step.content, () => {
          setCurrentStep((prev) => prev + 1);
        });
      }, 300);
    }
  };

  // Watch for step changes
  useEffect(() => {
    if (isAnimating && currentStep < animationSequence.length) {
      executeStep(animationSequence[currentStep]);
    } else if (isAnimating && currentStep >= animationSequence.length) {
      executeStep(null);
    }
  }, [currentStep, isAnimating]);

  // Reset animation
  const resetAnimation = () => {
    setMessages([]);
    setCurrentStep(0);
    setUserInput('');
    setIsTyping(false);
    startAnimation();
  };

  // Start animation
  const startAnimation = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentStep(0);
  };

  return (
    <div style={styles.container}>


      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.3 }}
        style={styles.demoContainer}
      >
        <div style={styles.aiConnectorCard}>
          <div style={styles.aiConnectorHeader}>
            <div>
              <div style={styles.aiConnectorTitle}>
                <Sparkles size={24} />
                Your AI Connector
              </div>
              <div style={styles.aiConnectorSubtitle}>
                Find and connect with valuable LinkedIn contacts
              </div>
            </div>
          </div>

          <div style={styles.chatContainer}>
            {messages.map((message, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  ...styles.chatMessage,
                  ...(message.role === 'user' ? styles.userMessage : styles.assistantMessage),
                }}
              >
                <div
                  style={{
                    ...styles.messageBubble,
                    ...(message.role === 'user' ? styles.userBubble : styles.assistantBubble),
                  }}
                >
                  {message.content}
                </div>

                {message.suggestions && message.suggestions.length > 0 && (
                  <div style={styles.connectionResultsContainer}>
                    <div style={styles.resultsHeader}>
                      <Users size={18} />
                      Found {message.suggestions.length} connection
                      {message.suggestions.length !== 1 ? 's' : ''}
                    </div>
                    {message.suggestions.map((suggestion, sIdx) => {
                      const priorityColor = getPriorityColor(suggestion.priority);
                      return (
                        <motion.div
                          key={sIdx}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: sIdx * 0.1 }}
                          style={styles.suggestionCard}
                        >
                          <div style={styles.suggestionHeader}>
                            <div style={{ flex: 1 }}>
                              <div style={styles.suggestionTitle}>{suggestion.name}</div>
                              <div style={styles.suggestionReason}>{suggestion.reasoning}</div>
                            </div>
                            <span
                              style={{
                                ...styles.priorityBadge,
                                background: priorityColor.bg,
                                color: priorityColor.text,
                              }}
                            >
                              {suggestion.priority}
                            </span>
                          </div>
                          <div style={styles.suggestionDetails}>
                            <div>
                              <strong>Role:</strong> {suggestion.current_role} at{' '}
                              {suggestion.company}
                            </div>
                            <div style={{ marginTop: '0.25rem' }}>
                              <strong>Location:</strong> {suggestion.location}
                            </div>
                            {suggestion.common_ground && (
                              <div style={{ marginTop: '0.25rem' }}>
                                <strong>Common Ground:</strong> {suggestion.common_ground}
                              </div>
                            )}
                          </div>
                          <div style={styles.suggestionCta}>
                            Click to add and schedule outreach
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            ))}

            {isTyping && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ ...styles.chatMessage, ...styles.assistantMessage }}
              >
                <div style={{ ...styles.messageBubble, ...styles.assistantBubble }}>
                  <div style={styles.typingIndicator}>
                    <Loader size={16} className="animate-spin" style={{ animation: 'spin 2s linear infinite' }} />
                    Searching LinkedIn...
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={chatEndRef} />
          </div>

          <div style={styles.chatInputContainer}>
            <div style={styles.chatInput}>
              <textarea
                value={userInput}
                readOnly
                placeholder='e.g., "Find VCs in Boston" or "Software engineers from MIT"'
                style={styles.textarea}
                rows={1}
              />
              <button style={styles.sendButton}>
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          style={styles.ctaSection}
        >
          <button
            style={styles.ctaButton}
            onClick={() => (window.location.href = '/register')}
          >
            <span>Start Finding Connections</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <p style={styles.ctaSubtext}>Watch the demo loop or sign up to get started</p>
        </motion.div> */}
      </motion.div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Inline styles object
const styles = {
  container: {
    position: 'relative',
    zIndex: 1,
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '60px 20px',
  },
  heroSection: {
    textAlign: 'center',
    marginBottom: '60px',
  },
  mainTitle: {
    fontSize: '3.5rem',
    fontWeight: 700,
    marginBottom: '20px',
    letterSpacing: '-0.02em',
    color: '#ffffff',
  },
  gradientText: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subtitle: {
    fontSize: '1.25rem',
    color: '#a0a0b8',
    fontWeight: 400,
  },
  demoContainer: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  aiConnectorCard: {
    background: '#1a1a2e',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 30px 80px rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  aiConnectorHeader: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  aiConnectorTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '1.5rem',
    fontWeight: 600,
    color: 'white',
    marginBottom: '8px',
  },
  aiConnectorSubtitle: {
    fontSize: '0.95rem',
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: 400,
  },
  chatContainer: {
    padding: '24px',
    minHeight: '500px',
    maxHeight: '600px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  chatMessage: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  userMessage: {
    alignItems: 'flex-end',
  },
  assistantMessage: {
    alignItems: 'flex-start',
  },
  messageBubble: {
    padding: '14px 18px',
    borderRadius: '12px',
    maxWidth: '85%',
    lineHeight: 1.6,
    fontSize: '0.95rem',
  },
  userBubble: {
    background: 'linear-gradient(135deg, rgba(79, 172, 254, 0.2) 0%, rgba(0, 242, 254, 0.2) 100%)',
    border: '1px solid rgba(79, 172, 254, 0.3)',
    color: '#ffffff',
  },
  assistantBubble: {
    background: '#16213e',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
  },
  connectionResultsContainer: {
    width: '100%',
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  resultsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#ffffff',
    padding: '8px 0',
  },
  suggestionCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#1e293b',
  },
  suggestionHeader: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
  },
  suggestionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '8px',
  },
  suggestionReason: {
    fontSize: '0.875rem',
    color: '#475569',
    lineHeight: 1.5,
  },
  priorityBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
  },
  suggestionDetails: {
    fontSize: '0.8rem',
    color: '#64748b',
    lineHeight: 1.6,
  },
  suggestionCta: {
    marginTop: '12px',
    padding: '10px',
    background: '#eff6ff',
    borderRadius: '6px',
    fontSize: '0.75rem',
    color: '#1e40af',
    textAlign: 'center',
    fontWeight: 500,
  },
  typingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#667eea',
    fontSize: '0.875rem',
  },
  chatInputContainer: {
    padding: '20px 24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  chatInput: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
  },
  textarea: {
    flex: 1,
    background: '#16213e',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '14px 18px',
    color: '#ffffff',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    resize: 'none',
    minHeight: '48px',
    maxHeight: '120px',
  },
  sendButton: {
    width: '48px',
    height: '48px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    flexShrink: 0,
  },
  ctaSection: {
    textAlign: 'center',
    marginTop: '40px',
  },
  ctaButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    padding: '16px 40px',
    borderRadius: '16px',
    color: 'white',
    fontSize: '1.125rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
    fontFamily: 'inherit',
  },
  ctaSubtext: {
    marginTop: '16px',
    color: '#a0a0b8',
    fontSize: '0.95rem',
  },
};

export default AIConnectorDemo;